import useLocalStorageState from "use-local-storage-state"
import { useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { GetRequest, PutRequest } from "../Utils/hooks/ApiHandler#"
import { toast } from "sonner"
import Card from "react-bootstrap/Card"
import { Controller, useForm } from "react-hook-form"
import { Button, Form, Row, Col } from "react-bootstrap"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import Select from "react-select"
import {
    IconReceiptTax,
    IconCreditCard,
    IconBuildingBank,
    IconInfoCircle,
    IconCheck,
} from "@tabler/icons-react"
import { BsArrowLeft } from "react-icons/bs"

// --- Schema ---
// const setupSchema = z.object({
//   pan: z
//     .string()
//     .min(10, "PAN is usually 10 characters")
//     .optional()
//     .or(z.literal("")),
//   gst_treatment: z.string().min(1, "GST Treatment is required"),
//   gstn: z.string().optional(),
//   bussines_legal_name: z.string().optional(),
// })

const setupSchema = z.object({
    pan: z
        .string()
        .length(10, "PAN must be exactly 10 characters"),
    gst_treatment: z.string().min(1, "Please select GST Treatment"),
    gstn: z.string().optional(),
    bussines_legal_name: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.gst_treatment === "register") {
        if (!data.gstn || data.gstn.length !== 15) {
            ctx.addIssue({
                path: ["gstn"],
                message: "GSTIN must be exactly 15 characters",
                code: z.ZodIssueCode.custom,
            })
        }

        if (!data.bussines_legal_name || !data.bussines_legal_name.trim()) {
            ctx.addIssue({
                path: ["bussines_legal_name"],
                message: "Business legal name is required",
                code: z.ZodIssueCode.custom,
            })
        }
    }
})
// .superRefine((data, ctx) => {
//   // 3. Conditional Logic: If treatment is "2" (Registered), enforce rules
//   if (data.gst_treatment === "2") {
//     // Validate GSTN
//     if (!data.gstn || data.gstn.length !== 16) {
//       // Note: Standard Indian GSTIN is usually 15 chars, check if 16 is intended
//       ctx.addIssue({
//         code: z.ZodIssueCode.custom,
//         message: "GSTIN must be exactly 16 characters",
//         path: ["gstn"],
//       })
//     }

//     // Validate Business Name
//     if (
//       !data.bussines_legal_name ||
//       data.bussines_legal_name.trim().length === 0
//     ) {
//       ctx.addIssue({
//         code: z.ZodIssueCode.custom,
//         message: "Business legal name is required",
//         path: ["bussines_legal_name"],
//       })
//     }
//   }
// })

// --- Constants ---
// const THEME_COLOR = "#7a3a5e"
// const THEME_RGBA_SHADOW = "rgba(122, 58, 94, 0.64)" // #7a3a5e converted to rgb with opacity
// const THEME_LIGHT_BG = "#fcf4f8" // Very light tint of the theme color for hover states
// ... existing imports

// --- Constants ---
const THEME_COLOR = "#7a3a5e"
// CHANGED: Reduced opacity from 0.64 to 0.25 to make it lighter/softer
const THEME_RGBA_SHADOW = "rgba(122, 58, 94, 0.1)"
const THEME_LIGHT_BG = "#fcf4f8"

// ... existing component code

export const SetupGst = () => {
    const {
        handleSubmit,
        register,
        reset,
        control,
        formState: { errors, isDirty },
    } = useForm({
        resolver: zodResolver(setupSchema),
        mode: "onChange",
        defaultValues: {
            gstn: "",
            pan: "",
            bussines_legal_name: "",
            gst_treatment: "",
        },
    })


    // --- State & Hooks ---
    const [customer, setCustomer] = useLocalStorageState("customer")
    const [setup, setSetup] = useState({})
    const [isShow, setIsShow] = useState(false)
    const navigate = useNavigate()

    const gst_Treatment = [
        { key: null, value: "", label: "Select GST Treatment", description: "-" },
        {
            key: "1",
            value: "unregister",
            label: "Unregistered Business",
            description: "Business not registered under GST",
        },
        {
            key: "2",
            value: "register",
            label: "Registered Business - Regular",
            description: "Business registered under GST",
        },
    ]

    // --- API Configuration ---
    const headers = {
        Authorization: customer?.token?.authorization,
        Authentication: customer?.token?.authentication,
    }
    const rf = customer?.token?.authentication
    const extras = { customer, setCustomer, rf, navigate }
    const customer_id = customer?.customer_details?.customer_id

    // --- Effects ---
    useEffect(() => {
        if (customer?.customer_details?.user_type !== "1") {
            navigate("/dashboard")
        }
    }, [customer, navigate])

    // Fetch Data
    useEffect(() => {
        ; (async () => {
            const res = await GetRequest(
                `profile/fetchGstDetails/${customer_id}`,
                { headers },
                { ...extras, rf: headers.Authentication }
            )
            if (res.err) {
                toast.error("Error Fetching Data")
                return
            }
            setSetup(res.data || {})
        })()
    }, [customer, customer_id])

    // Populate Form & Handle Initial Visibility
    useEffect(() => {
        if (setup) {
            reset({
                gstn: setup.gstn || "",
                pan: setup?.pan || "",
                bussines_legal_name: setup?.bussines_legal_name || "",
                gst_treatment: setup.gst_treatment || "",
            })

            const treatment = gst_Treatment.find(
                (t) => t.value === setup.gst_treatment
            )
            if (treatment?.key === "2") {
                setIsShow(true)
            }
        }
    }, [setup, reset])

    // --- Handlers ---
    const onSubmit = async (formData) => {
        try {
            let payload
            if (formData.gst_treatment === "unregister") {
                payload = {
                    gstn: "",
                    bussines_legal_name: "",
                    gst_treatment: formData?.gst_treatment,
                    pan: formData?.gst_treatment,
                    customer_id: customer_id,
                }
            } else {
                payload = { ...formData, pan: formData.gstn?.slice(2, formData.gstn?.length - 3), customer_id: customer_id }
            }

            const res = await PutRequest(
                "profile/updateGstDetails/",
                payload,
                { headers },
                extras
            )

            if (res?.err) {
                toast.error("Something went wrong!")
                return
            }
            if (res.status === "success") {
                toast.success("GST Details updated successfully!")

                const updatedRes = await GetRequest(
                    `profile/fetchGstDetails/${customer_id}`,
                    { headers },
                    { ...extras, rf: headers.Authentication }
                )

                if (!updatedRes.err) {
                    setSetup(updatedRes.data || {})
                    reset({
                        gstn: updatedRes.data?.gstn || "",
                        pan: updatedRes.data?.pan || "",
                        bussines_legal_name: updatedRes.data?.bussines_legal_name || "",
                        gst_treatment: updatedRes.data?.gst_treatment || "",
                    })
                }
            }
        } catch (error) {
            console.error(error)
            toast.error("Update failed")
        }
    }

    // --- Custom Select Styles (Updated with #7a3a5e) ---
    // --- Custom Select Styles (Updated) ---
    const customStyless = {
        control: (provided, state) => ({
            ...provided,
            // CHANGED: Enforce fixed height to prevent jumping on open
            minHeight: "38px",
            height: "38px",
            // CHANGED: Adjust padding to center text within the fixed height
            padding: "0 0.5rem",
            borderRadius: "0.3rem",
            borderColor: state.isFocused ? "#7a3a5eeb" : "#dee2e6",
            // CHANGED: Use the softer shadow constant, spread radius set to 4px to match Bootstrap
            boxShadow: state.isFocused ? `0 0 0 1px ${THEME_RGBA_SHADOW}` : "none",
            "&:hover": { borderColor: THEME_COLOR },
        }),
        option: (provided, state) => ({
            // ... (keep existing option logic)
            ...provided,
            backgroundColor: state.isSelected
                ? "#7a3a5eeb"
                : state.isFocused
                    ? THEME_LIGHT_BG
                    : "white",
            color: state.isSelected ? "white" : "#1f2937",
        }),
        singleValue: (provided) => ({
            ...provided,
            color: "#212529",
            fontWeight: 500,
            fontSize: 14,
        }),
        // CHANGED: Ensure the input container doesn't add extra margin
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
        }),
        // ... (keep existing menu logic)
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            borderRadius: "0.5rem",
            boxShadow: "0 8px 15px -3px rgba(0, 0, 0, 0.1)",
            fontSize: 14,
        }),
    }

    const CustomOption = ({ innerRef, innerProps, data, isSelected }) => (
        <div
            ref={innerRef}
            {...innerProps}
            className={`px-3 py-2 cursor-pointer`}
            style={{
                cursor: "pointer",
                backgroundColor: isSelected
                    ? "#7a3a5eeb"
                    : innerProps.className?.includes("focused")
                        ? THEME_LIGHT_BG
                        : "transparent",
            }}
        >
            <div
                className="fw-medium"
                style={{ color: isSelected ? "white" : "#333" }}
            >
                {data.label}
            </div>
            <div
                className="small"
                style={{
                    color: isSelected ? "#e0e7ff" : "#6c757d",
                    fontSize: "0.8rem",
                }}
            >
                {data.description}
            </div>
        </div>
    )

    return (
        <>
            <div className="container">
                {/* Page Header */}
                {/* <div className="mb-3 d-flex justify-content-between align-items-end">
          <div>
            <h6
              className="addEnquiryHead"
              style={{ fontWeight: "bold", color: "black" }}
            >
              Tax & Statutory
            </h6>
            <p className="text-muted mb-0 small">
              Manage your GST registration and PAN details.
            </p>
          </div>
          <Button
            className="btn btn-default-outline"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft className="me-1" /> Back
          </Button>
        </div> */}
                <div className="mb-3 d-flex justify-content-between align-items-end">
                    <div style={{ lineHeight: "0.8rem" }}>
                        <h6
                            className="addEnquiryHead"
                            style={{ fontWeight: "400", color: "black", fontSize: "1.13rem" }}
                        >
                            Tax & Statutory
                        </h6>
                        <p className="text-muted mb-0 small">
                            Manage your GST registration and PAN details.
                        </p>
                    </div>
                    <Button className="btn btn-default" onClick={() => navigate(-1)}>
                        <BsArrowLeft className="me-1" /> Back
                    </Button>
                </div>
                {/* <div className="mb-4 ">
          <h6
            className="addEnquiryHead"
            style={{ fontWeight: "bold", color: "black" }}
          >
            Tax & Statutory
          </h6>
          <p className="text-muted small">
            Manage your GST registration and PAN details.
          </p>
        </div> */}
                <hr
                    className="mt-3 mb-4"
                    style={{
                        borderTop: "0.5px solid #6c757d",
                        opacity: "0.3",
                    }}
                />
                <Row>
                    {/* Left Column: Form */}
                    <Col lg={8}>
                        <Card
                            className="customBorderforCard"
                            style={{ borderRadius: "6px" }}
                        >
                            <Card.Body className="p-3">
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    {/* Header Section of Form */}
                                    <div className="d-flex align-items-center mb-3 pb-3">
                                        <div
                                            className="bg-light rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "48px",
                                                height: "48px",
                                                border: "1px rgb(35 183 229 / 20%) solid",
                                            }}
                                        >
                                            <IconReceiptTax
                                                size={24}
                                                // className="text-muted"
                                                style={{ color: "rgb(35 183 229 / 94%)" }}
                                            />
                                        </div>
                                        <div>
                                            <h5
                                                className="mb-0"
                                                style={{
                                                    fontSize: "18px",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                General Tax Information
                                            </h5>
                                            <small className="text-muted">
                                                Enter your basic tax identification details.
                                            </small>
                                        </div>
                                    </div>
                                    <Row className="g-4 mb-4">
                                        {/* GST Treatment Selection */}
                                        <Col md={12}>
                                            <Form.Group controlId="gst_treatment">
                                                <Form.Label>
                                                    <p className="fs-14 text-black">
                                                        GST Treatment <span className="text-danger">*</span>
                                                    </p>
                                                    <p
                                                        className="text-muted mb-1 small"
                                                        style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                    >
                                                        Manage your company profile and branding. auhe
                                                    </p>
                                                </Form.Label>
                                                <Controller
                                                    name="gst_treatment"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            options={gst_Treatment}
                                                            value={
                                                                gst_Treatment.find(
                                                                    (gt) => gt.value === field.value
                                                                ) || null
                                                            }
                                                            onChange={(val) => {
                                                                field.onChange(val?.value || "")
                                                                if (val?.key === "2") {
                                                                    setIsShow(true)
                                                                } else {
                                                                    setIsShow(false)
                                                                }
                                                            }}
                                                            components={{ Option: CustomOption }}
                                                            styles={customStyless}
                                                            placeholder="Select how your business is registered..."
                                                            isClearable
                                                        />
                                                    )}
                                                />
                                                {errors?.gst_treatment && (
                                                    <p className="text-danger small mt-1">
                                                        {errors.gst_treatment.message}
                                                    </p>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        {/* PAN Number */}
                                        {!isShow && (
                                            <Col md={12}>
                                                <Form.Group controlId="panNo">
                                                    <Form.Label>
                                                        {/* <span className="fs-13">
                            Permanent Account Number (PAN)
                            <span className="text-danger">*</span>
                          </span> */}
                                                        <p className="fs-14 text-black">
                                                            Permanent Account Number (PAN)
                                                            <span className="text-danger">*</span>
                                                        </p>
                                                        <p
                                                            className="text-muted mb-1 small"
                                                            style={{ fontSize: "0.72rem", fontWeight: "300" }}
                                                        >
                                                            Manage your company profile and branding. auhe
                                                        </p>
                                                    </Form.Label>
                                                    <div className="input-group">
                                                        <span
                                                            className="input-group-text border-end-0"
                                                            style={{
                                                                background: "rgb(233 242 251 / 47%) !important",
                                                            }}
                                                        >
                                                            <IconCreditCard
                                                                size={18}
                                                                // className="text-muted"
                                                                style={{ color: "rgb(35 183 229 / 94%)" }}
                                                            />
                                                        </span>
                                                        <Form.Control
                                                            className="border-start-0 ps-1"
                                                            type="text"
                                                            placeholder="e.g. ABCDE1234F"
                                                            maxLength={10}
                                                            {...register("pan", {
                                                                setValueAs: (v) => v.trim().toUpperCase(),
                                                            })}
                                                            // isInvalid={!!errors?.pan}
                                                            style={{ textTransform: "uppercase" }}
                                                        />
                                                    </div>
                                                    {/* <Form.Control.Feedback type="invalid">
                          {errors?.pan?.message}
                        </Form.Control.Feedback> */}
                                                    {errors?.pan && (
                                                        <p className="text-danger small mt-1">
                                                            {errors?.pan?.message}
                                                        </p>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </Row>
                                    {/* Conditional Section: GST Details */}
                                    {isShow && (
                                        <div className="bg-light rounded-2 p-4 mb-4 mt-1 border border-light">
                                            <h6
                                                className="text-dark mb-3 d-flex align-items-center"
                                                style={{
                                                    fontSize: "16px",
                                                    fontWeight: "400",
                                                }}
                                            >
                                                <IconBuildingBank
                                                    size={18}
                                                    className="me-2"
                                                    style={{ color: "rgb(35 183 229 / 94%)" }}
                                                />
                                                Registration Details
                                            </h6>
                                            <Row className="g-3">
                                                <Col md={6} sm={12}>
                                                    <Form.Group controlId="gstn">
                                                        <Form.Label>
                                                            <p className="fs-14 text-black">
                                                                GST Identification Number (GSTIN)
                                                                <span className="text-danger">*</span>
                                                            </p>
                                                            <p
                                                                className="text-muted mb-1 small"
                                                                style={{
                                                                    fontSize: "0.72rem",
                                                                    fontWeight: "300",
                                                                }}
                                                            >
                                                                Manage your company profile and branding. auhe
                                                            </p>
                                                            {/* <span className="fs-13">
                                GST Identification Number (GSTIN)
                                <span className="text-danger">*</span>
                              </span> */}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="e.g. 22AAAAA0000A1Z5"
                                                            maxLength={15}
                                                            {...register("gstn")}
                                                            isInvalid={!!errors?.gstn}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors?.gstn?.message}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6} sm={12}>
                                                    <Form.Group controlId="business_legal_name">
                                                        <Form.Label>
                                                            <p className="fs-14 text-black">
                                                                Legal Business Name
                                                                <span className="text-danger">*</span>
                                                            </p>
                                                            <p
                                                                className="text-muted mb-1 small"
                                                                style={{
                                                                    fontSize: "0.72rem",
                                                                    fontWeight: "300",
                                                                }}
                                                            >
                                                                Manage your company profile and branding. auhe
                                                            </p>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="As per GST Certificate"
                                                            {...register("bussines_legal_name")}
                                                            isInvalid={!!errors?.bussines_legal_name}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors?.bussines_legal_name?.message}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}
                                    {/* Footer Actions */}
                                    <div className="d-flex justify-content-end pt-4 border-top mt-3">
                                        {/* <Button
                                        type="button"
                                        variant="light"
                                        className="me-2 text-muted"
                                        onClick={() => navigate(-1)}
                                        >
                                        Cancel
                                        </Button> */}
                                        <Button
                                            type="submit"
                                            className="px-3 fw-medium btn btn-default"
                                            disabled={!isDirty}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Right Column: Info / Helper */}
                    <Col lg={4} className="mt-4 mt-lg-0">
                        <Card
                            className="border-0 shadow-sm text-white"
                            style={{
                                borderRadius: "6px",
                                // Updated gradient to match #7a3a5e
                                background: `linear-gradient(135deg, rgb(255 215 170) 0%, rgb(255 215 171 / 40%) 100%)`,
                            }}
                        >
                            <Card.Body className="p-4 ">
                                <div className="mb-3 opacity-75">
                                    <IconInfoCircle size={32} style={{ color: "#7a3a5e" }} />
                                </div>
                                <h5 className="fw-bold" style={{ color: "#7a3a5e" }}>
                                    Why do we need this?
                                </h5>
                                <p
                                    className="small mb-4"
                                // style={{ color: "#212529e3" }}
                                >
                                    Your GSTIN and PAN details are required to generate compliant
                                    invoices and for tax filing purposes.
                                </p>

                                <div className="d-flex align-items-center mb-2">
                                    <IconCheck
                                        size={16}
                                        className="me-2"
                                        style={{ color: "#7a3a5e" }}
                                    />
                                    <span
                                        className="small fw-medium"
                                        style={{ color: "#7a3a5e" }}
                                    >
                                        Automated Tax Calculations
                                    </span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <IconCheck
                                        size={16}
                                        className="me-2"
                                        style={{ color: "#7a3a5e" }}
                                    />
                                    <span
                                        className="small fw-medium"
                                        style={{ color: "#7a3a5e" }}
                                    >
                                        Compliant B2B Invoices
                                    </span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <IconCheck
                                        size={16}
                                        className="me-2"
                                        style={{ color: "#7a3a5e" }}
                                    />
                                    <span
                                        className="small fw-medium"
                                        style={{ color: "#7a3a5e" }}
                                    >
                                        ITC Claiming
                                    </span>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            <style jsx>{`
        .css-1xc3v61-indicatorContainer {
          display: -ms-flexbox;
          display: flex;
          transition: color 150ms;
          color: hsl(0, 0%, 80%);
          padding: 5px;
          box-sizing: border-box;
        }

        .css-1u9des2-indicatorSeparator {
          -ms-flex-item-align: stretch;
          align-self: stretch;
          width: 1px;
          background-color: hsl(0, 0%, 80%);
          margin-bottom: 3px;
          margin-top: 3px;
          box-sizing: border-box;
        }

           .customBorderforCard {
          border: 1px solid #d4e9f0 !important;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4b5563;
          margin-bottom: 0.4rem;
        }

        
        // .form-control:focus {
        //   border-color: none;
        //   box-shadow: 0 0 0 1px ${THEME_RGBA_SHADOW};
        // }

        .input-group-text {
          background-color: #f9fafb;
          border-color: #dee2e6;
        }

        /* Custom Theme Classes to override Bootstrap Primary */
        .text-theme-primary {
          color: ${THEME_COLOR} !important;

        .btn-theme-primary {
          background-color: ${THEME_COLOR};
          border-color: ${THEME_COLOR};
          color: white;
        }

        .btn-theme-primary:hover {
          background-color: #632f4c; /* Slightly darker shade for hover */
          border-color: #552841;
          color: white;
        }

        // .form-control:focus {
        //   border-color: ${THEME_COLOR};
        //   /* CHANGED: Uses the new lighter opacity variable */
        //   box-shadow: 0 0 0 4px ${THEME_RGBA_SHADOW};
        // }

        .btn-theme-primary:disabled {
          background-color: ${THEME_COLOR};
          border-color: ${THEME_COLOR};
          opacity: 0.65;
        }
      `}</style>
        </>
    )
}
